import { createSlice, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
    dismissAllNotifications,
    getNotifications,
    setNotificationSeen,
    setNotificationViewed,
} from '../actions/notificationActions';
import { Device } from './device';

export const notificationAdapter = createEntityAdapter();

export const NotificationTypes = {
    WELCOME: 'WELCOME',
    TEAM_INVITATION: 'TEAM_INVITATION',
    TEAM_UPDATE: 'TEAM_UPDATE',
    PLANNED_HUNT_INVITATION: 'PLANNED_HUNT_INVITATION',
    PLANNED_HUNT_CANCELLED: 'PLANNED_HUNT_CANCELLED',
    PLANNED_HUNT_UPDATE: 'PLANNED_HUNT_UPDATE',
    HUNT_STATUS: 'HUNT_STATUS',
    HUNT_SHARING: 'HUNT_SHARING',
    INFO: 'INFO',
    LOW_BATTERY: 'LOW_BATTERY',
    CONTACT_REQUEST: 'CONTACT_REQUEST',
    CONTACT_UPDATE: 'CONTACT_UPDATE',
    GENERAL: 'GENERAL',
    ALARM_BARK: 'ALARM_BARK',
    GEOFENCE_EXIT: 'GEOFENCE_EXIT',
    GEOFENCE_ENTER: 'GEOFENCE_ENTER',
    SPEED_EXCEEDED: 'SPEED_EXCEEDED',
    POWER_OFF: 'POWER_OFF',
    STOPPED: 'STOPPED',
};

type Coordinate = {
    latitude: number;
    longitude: number;
};
export type Notification = {
    id: number;
    critical: number;
    seen: number;
    viewed: number;
    title: string;
    team_name?: string;
    timestamp: string;
    created: string;
    type: string;
    data: {
        isWatcher?: boolean;
        link?: string;
        data?: {
            requestId: number;
            photo: string;
            title?: string;
            inviteId: number;
            teamName?: string;
            teamMembers: string[];
            teamPhoto?: string;
            status?: string;
            coords?: Coordinate[] | Coordinate;
            teamId?: number;
            location?: string;
            description?: string;
            deviceName?: string;
            devices?: Device[];
            user?: number;
        };
    };
    body: string;
};
const INITIAL_STATE = {
    isNotificationWelcome: true,
    isNotificationVisible: false,
    notifications: [] as Notification[],
    hasCritical: false,
};

const notificationsReducer = createSlice({
    name: 'notifications',
    initialState: notificationAdapter.getInitialState(INITIAL_STATE),
    reducers: {
        setNotificationWelcomeStatus(state, action) {
            state.isNotificationWelcome = action.payload;
        },
        setNotificationVisible(state, action) {
            state.isNotificationVisible = action.payload;
        },
        addNotification(state, action) {
            state.notifications = [...state.notifications, action.payload];
        },
        dismissNotification(state, action) {
            const notifs = state.notifications.filter((not) => action.payload.id !== not.id);
            state.notifications = notifs;
            if (notifs.length === 0) {
                state.isNotificationVisible = false;
            }
        },
        setNotifications(state, action) {
            state.notifications = action.payload;
            state.hasCritical = action.payload.some((n: Notification) => n.critical);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getNotifications.fulfilled, (state, action) => {
                state.notifications = action.payload;
            })
            .addCase(getNotifications.rejected, () => {})
            .addCase(setNotificationSeen.fulfilled, (state, action) => {
                state.notifications = state.notifications.filter((not) => not.id !== action.payload);
            })
            .addCase(setNotificationSeen.rejected, () => {})
            .addCase(dismissAllNotifications.fulfilled, (state) => {
                state.notifications = [];
            })
            .addCase(dismissAllNotifications.rejected, () => {})
            .addCase(setNotificationViewed.fulfilled, (state, action) => {
                state.notifications = state.notifications.map((not) => {
                    if (not.id === action.payload) {
                        not.viewed = 1;
                    }

                    return not;
                });
            })
            .addCase(setNotificationViewed.rejected, () => {});
    },
});

export const {
    addNotification,
    dismissNotification,
    setNotificationWelcomeStatus,
    setNotificationVisible,
    setNotifications,
} = notificationsReducer.actions;

export default notificationsReducer.reducer;

const notifState$ = (s: RootState) => s.notification;

export const rawNotifs$ = createSelector(notifState$, (s) => s.notifications);

export const notSeenNotifs$ = createSelector(rawNotifs$, (notifications) => notifications?.some((n) => n.seen === 0));

export const notViewedNotifs$ = createSelector(
    rawNotifs$,
    (notifications) => notifications?.some((n) => n.viewed === 0),
);
